/**
 * The broiler hen for Beat 8.
 *
 * The silhouette is deliberately economical: a compact breast, a head carried
 * above it, a hard beak wedge, a lifted tail, and two narrow bird legs.  Those
 * five relationships are the read at gameplay distance; the small comb and
 * wings only reinforce it.  Static geometry stays in one group while the named
 * pivots give the scatter director independent handles later.
 */
import * as THREE from 'three';
import { surface } from './materials';
import type { AssetDefinition } from './types';

/**
 * A broiler is a WHITE bird, and this is a correction made in the room.
 *
 * The first build took the three surfaces from `FINISHER` — the pig palette —
 * because a flock and a rack of finishers are both stock and the reuse looked
 * principled. On the workbench it passed its gate and looked fine. In the
 * fattening hall, which is the only place these are ever seen, four of them
 * standing next to Rosalie read as **four small piglets**: same hue, same
 * material, same toy. The silhouette gate is about shape and it was passing;
 * this is the half of "it must read as not a mammal" that shape does not cover.
 *
 * So: bone-white and a cold grey shadow, which is a commercial broiler and is
 * nothing else in this game's palette. Still three surfaces, so the flock is
 * still three instanced meshes and the budget row does not move.
 *
 * Authored here rather than in `palette.ts` deliberately — `palette.ts` holds the
 * art direction for ZONES and characters that recur, and one bird in one beat is
 * neither. If the hen comes back in Act 3 it earns an entry there.
 */
const HEN = {
  /** Dirty white. Not pure: nothing in this building is clean. */
  feather: 0xd8d2c4,
  /** Cold, not warm. A pig's shadow is brown; a white bird's is grey-blue. */
  featherShadow: 0x8e8d8a,
  /** Legs and beak-shadow. Broiler shanks are yellow-grey, not black. */
  shank: 0x6b5f4b,
} as const;

export const HEN_SCALE = { width: 0.20, height: 0.41, length: 0.44 } as const;

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

function legGeometry(from: THREE.Vector3, to: THREE.Vector3, radius: number): THREE.BufferGeometry {
  const direction = to.clone().sub(from);
  const geometry = new THREE.CylinderGeometry(radius, radius * 1.12, direction.length(), 6);
  geometry.translate(0, direction.length() / 2, 0);
  const axis = new THREE.Vector3(0, 1, 0);
  geometry.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(axis, direction.normalize()));
  geometry.translate(from.x, from.y, from.z);
  return geometry;
}

function build(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'hen';
  // The assembled extrema are fractionally beak-heavy; centre the measured
  // footprint so placement at (x, 0, z) does not need a scene-side offset.
  root.position.z = -0.014;

  const feather = surface('fabric', HEN.feather);
  const shadowFeather = surface('fabric', HEN.featherShadow);
  const dark = surface('organic', HEN.shank);

  // A low-poly breast gives a faceted, toy-made body rather than a mascot blob.
  const body = mesh(new THREE.SphereGeometry(0.115, 12, 8), feather, 'hen-body');
  body.scale.set(0.92, 0.84, 1.20);
  body.position.set(0, 0.215, 0.0);
  root.add(body);

  const headPivot = new THREE.Object3D();
  headPivot.name = 'hen-head-pivot';
  headPivot.position.set(0, 0.325, 0.105);
  const head = mesh(new THREE.SphereGeometry(0.072, 10, 7), feather, 'hen-head');
  head.scale.set(0.88, 1.05, 0.94);
  headPivot.add(head);
  root.add(headPivot);

  // A four-sided wedge makes the forward direction and species read survive
  // silhouette mode where all surface colour is removed.
  const beak = mesh(new THREE.ConeGeometry(0.035, 0.095, 4, 1), shadowFeather, 'hen-beak');
  beak.rotation.x = Math.PI / 2;
  beak.position.set(0, 0.322, 0.192);
  root.add(beak);

  const comb = mesh(new THREE.ConeGeometry(0.024, 0.055, 5, 1), shadowFeather, 'hen-comb');
  comb.position.set(0, 0.404, 0.102);
  comb.rotation.z = Math.PI;
  root.add(comb);

  // The raised rear wedge is more important than feather detail: it prevents
  // the body from collapsing into a mammal-like oval in profile.
  const tail = mesh(new THREE.ConeGeometry(0.10, 0.14, 5, 1), shadowFeather, 'hen-tail-fan');
  tail.rotation.x = -Math.PI / 2;
  tail.rotation.z = Math.PI / 2;
  tail.position.set(0, 0.285, -0.142);
  tail.scale.set(0.66, 1, 0.8);
  root.add(tail);

  for (const side of [-1, 1] as const) {
    const wingPivot = new THREE.Object3D();
    wingPivot.name = side < 0 ? 'hen-wing-left-pivot' : 'hen-wing-right-pivot';
    wingPivot.position.set(side * 0.092, 0.235, 0.015);
    const wing = mesh(new THREE.SphereGeometry(0.065, 8, 6), shadowFeather, side < 0 ? 'hen-wing-left' : 'hen-wing-right');
    wing.scale.set(0.34, 0.90, 1.18);
    wing.rotation.x = side * 0.15;
    wing.position.set(0, -0.005, 0.005);
    wingPivot.add(wing);
    root.add(wingPivot);
  }

  for (const side of [-1, 1] as const) {
    const legPivot = new THREE.Object3D();
    legPivot.name = side < 0 ? 'hen-leg-left-pivot' : 'hen-leg-right-pivot';
    const x = side * 0.052;
    legPivot.position.set(x, 0, 0.015);
    const upper = mesh(legGeometry(new THREE.Vector3(0, 0.125, 0.0), new THREE.Vector3(0, 0.052, 0.018), 0.010), dark, side < 0 ? 'hen-leg-left' : 'hen-leg-right');
    // The ankle is a little below the nominal floor centre so the tapered
    // circular end actually kisses y=0 after the shin's diagonal rotation.
    // This preserves the bird's stance while satisfying the shared ground
    // contact contract without translating the whole character.
    const lower = mesh(legGeometry(new THREE.Vector3(0, 0.052, 0.018), new THREE.Vector3(0, 0.005, -0.018), 0.008), dark, side < 0 ? 'hen-shin-left' : 'hen-shin-right');
    legPivot.add(upper, lower);
    root.add(legPivot);
  }

  return root;
}

export const henAsset: AssetDefinition = {
  id: 'hen',
  label: 'Broiler hen',
  description: 'Small restrained procedural bird for the Beat 8 crate scatter.',
  footprint: HEN_SCALE,
  create: build,
};
